import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ArrowLeft, Send, Bot, User, Loader2, MessageSquare, Plus, Trash2, History } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chat() {
  const [models, setModels] = useState([]);
  const [model, setModel] = useState('gpt-5.2');
  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [showSessions, setShowSessions] = useState(false);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/chat/models`).then(r => setModels(r.data?.models || []));
    loadSessions();
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const loadSessions = async () => {
    try {
      const r = await axios.get(`${API}/chat/sessions`, { withCredentials: true });
      setSessions(r.data?.sessions || []);
    } catch { /* ignore */ }
  };

  const openSession = async (sid) => {
    setSessionId(sid);
    try {
      const r = await axios.get(`${API}/chat/history/${sid}`, { withCredentials: true });
      setMessages(r.data?.messages || []);
    } catch { /* ignore */ }
  };

  const newSession = () => {
    setSessionId(null);
    setMessages([]);
  };

  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this chat?')) return;
    try {
      await axios.delete(`${API}/chat/sessions/${sid}`, { withCredentials: true });
      setSessions(s => s.filter(x => x.session_id !== sid));
      if (sessionId === sid) newSession();
    } catch { /* ignore */ }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;

    const userMsg = { role: 'user', content: text, created_at: new Date().toISOString() };
    const asstStub = { role: 'assistant', content: '', model, created_at: new Date().toISOString() };
    setMessages(m => [...m, userMsg, asstStub]);
    setInput('');
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: text, session_id: sessionId, model }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let receivedSessionId = sessionId;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // Parse SSE frames
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';
        for (const f of frames) {
          const line = f.split('\n').find(l => l.startsWith('data:'));
          if (!line) continue;
          try {
            const j = JSON.parse(line.slice(5).trim());
            if (j.delta) {
              setMessages(m => {
                const copy = [...m];
                copy[copy.length - 1] = { ...copy[copy.length - 1], content: copy[copy.length - 1].content + j.delta };
                return copy;
              });
            } else if (j.done) {
              receivedSessionId = j.session_id;
            } else if (j.error) {
              setMessages(m => {
                const copy = [...m];
                const last = copy[copy.length - 1];
                if (last?.role === 'assistant' && !last.content) {
                  copy[copy.length - 1] = { ...last, content: '⚠ The AI couldn\u2019t answer right now. Please try again shortly.' };
                }
                return copy;
              });
              toast.error('AI is temporarily unavailable — please retry');
            }
          } catch { /* ignore */ }
        }
      }
      if (receivedSessionId && receivedSessionId !== sessionId) {
        setSessionId(receivedSessionId);
        loadSessions();
      } else if (sessionId) {
        loadSessions();
      }
    } catch (e) {
      if (e.name !== 'AbortError') toast.error(e.message || 'Chat error');
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col" data-testid="chat-page">
      <header className="border-b border-[#1E1E24] bg-[#0A0A0B]">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-6 py-4 flex items-center gap-3">
          <Link to="/dashboard" className="text-zinc-400 hover:text-white flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Dashboard</span>
          </Link>
          <button
            onClick={() => setShowSessions(v => !v)}
            className="md:hidden text-zinc-400 hover:text-white flex items-center gap-1 text-sm"
            data-testid="chat-sessions-toggle"
            aria-label="Chat history"
          >
            <History className="w-4 h-4" /> Chats
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2 text-xs">
            <span className="text-zinc-500 font-mono uppercase">Model</span>
            <select
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="bg-[#121214] border border-[#1E1E24] rounded-md px-2 py-1 text-sm text-white"
              data-testid="chat-model-select"
            >
              {models.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>
        </div>
      </header>

      <div className="flex-1 flex max-w-[1400px] w-full mx-auto overflow-hidden relative">
        {/* Sidebar — overlay drawer on phones, static column on md+ */}
        {showSessions && <div className="md:hidden absolute inset-0 bg-black/60 z-20" onClick={() => setShowSessions(false)} />}
        <aside className={`${showSessions ? 'flex' : 'hidden'} md:flex absolute md:static inset-y-0 left-0 z-30 w-64 border-r border-[#1E1E24] bg-[#0A0A0B] flex-col`}>
          <div className="p-3">
            <Button onClick={() => { newSession(); setShowSessions(false); }} className="w-full bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="chat-new">
              <Plus className="w-4 h-4 mr-1" /> New chat
            </Button>
            {sessions.length > 0 && (
              <button
                onClick={async () => {
                  if (!window.confirm('Clear ALL your chats permanently? This cannot be undone.')) return;
                  try {
                    await axios.delete(`${API}/chat/all`, { withCredentials: true });
                    setSessions([]);
                    setMessages([]);
                    setSessionId(null);
                    toast.success('All chats cleared');
                  } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
                }}
                className="mt-2 w-full text-[10px] font-mono uppercase text-zinc-500 hover:text-red-400"
                data-testid="chat-clear-all"
              >
                Clear all chats
              </button>
            )}
          </div>
          <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-1">
            {sessions.length === 0 ? (
              <p className="text-[10px] text-zinc-600 text-center py-4">No chats yet</p>
            ) : sessions.map(s => (
              <button
                key={s.session_id}
                onClick={() => { openSession(s.session_id); setShowSessions(false); }}
                className={`w-full text-left px-2 py-1.5 rounded-md text-xs group flex items-center gap-1
                  ${sessionId === s.session_id ? 'bg-[#06B6D4]/20 text-white' : 'text-zinc-400 hover:bg-white/5'}`}
                data-testid={`chat-session-${s.session_id}`}
              >
                <MessageSquare className="w-3 h-3 shrink-0" />
                <span className="flex-1 truncate">{s.title || 'Untitled'}</span>
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => deleteSession(s.session_id, e)}
                  onKeyDown={(e) => e.key === 'Enter' && deleteSession(s.session_id, e)}
                  className="opacity-0 group-hover:opacity-100 hover:text-red-400 cursor-pointer"
                >
                  <Trash2 className="w-3 h-3" />
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* Chat */}
        <main className="flex-1 flex flex-col bg-[#050505] overflow-hidden">
          <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4">
            {messages.length === 0 && !streaming && (
              <div className="text-center py-16">
                <Bot className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-sm text-zinc-500">Ask anything about your game — mechanics, story, art direction, code.</p>
                <p className="text-[11px] text-zinc-600 mt-1 font-mono">Powered by {model.toUpperCase()} · streaming</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-3 ${m.role === 'user' ? 'justify-end' : ''}`}>
                {m.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-[#06B6D4]/20 flex items-center justify-center shrink-0">
                    <Bot className="w-4 h-4 text-[#06B6D4]" />
                  </div>
                )}
                <div className={`max-w-[75%] p-3 rounded-lg text-sm whitespace-pre-wrap
                  ${m.role === 'user' ? 'bg-[#06B6D4] text-black' : 'bg-[#0A0A0C] border border-[#1E1E24] text-zinc-200'}`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content || (streaming && i === messages.length - 1 && <Loader2 className="w-3 h-3 animate-spin inline" />)}
                </div>
                {m.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-zinc-700 flex items-center justify-center shrink-0">
                    <User className="w-4 h-4 text-zinc-300" />
                  </div>
                )}
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          <div className="border-t border-[#1E1E24] bg-[#0A0A0B] p-3">
            <div className="flex gap-2 max-w-3xl mx-auto">
              <Input
                data-testid="chat-input"
                placeholder="Type a message… (Enter to send)"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKey}
                disabled={streaming}
                className="bg-transparent border-[#1E1E24] text-sm"
              />
              <Button onClick={send} disabled={streaming || !input.trim()} className="bg-[#06B6D4] text-black hover:bg-[#06B6D4]/90" data-testid="chat-send">
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
